import React, { createContext, useContext, useEffect } from 'react';
import type { ThemeMode } from '@memorai/types';
import { getAccentForegroundColor } from '@memorai/ui/contrast';
import { useResolvedPreferences } from './i18n/useResolvedLocale';

interface ThemeConfig {
    mode: ThemeMode;
    primaryColor?: string; // Format: "R G B"
}

interface ThemeContextType {
    theme: ThemeConfig;
    setThemeMode: (mode: ThemeMode) => void;
    setPrimaryColor: (color: string) => void;
    isSaving: boolean;
    saveError: Error | null;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const hexToRgb = (hex: string): string | null => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
        ? `${parseInt(result[1], 16)} ${parseInt(result[2], 16)} ${parseInt(result[3], 16)}`
        : null;
};

export function ThemeProvider({ children }: { children: React.ReactNode }) {
    const {
        themeMode: mode,
        setThemeMode,
        primaryColor,
        setPrimaryColor: setResolvedPrimaryColor,
        isSaving,
        appearanceSaveError,
    } = useResolvedPreferences();

    useEffect(() => {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return;
        }

        const root = document.documentElement;

        const applyTheme = (currentMode: ThemeMode) => {
            let actualTheme = currentMode;
            if (currentMode === 'system' && typeof window.matchMedia === 'function') {
                actualTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
            }
            root.setAttribute('data-theme', actualTheme);
            root.classList.remove('light', 'dark');
            root.classList.add(actualTheme);
        };

        applyTheme(mode);

        if (mode === 'system' && typeof window.matchMedia === 'function') {
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            const handleChange = () => applyTheme('system');
            mediaQuery.addEventListener('change', handleChange);
            return () => mediaQuery.removeEventListener('change', handleChange);
        }
    }, [mode]);

    useEffect(() => {
        if (typeof document === 'undefined') {
            return;
        }

        const root = document.documentElement;
        root.style.setProperty('--primary-color', primaryColor);
        root.style.setProperty('--primary-foreground-color', getAccentForegroundColor(primaryColor));
    }, [primaryColor]);

    const setPrimaryColor = (color: string) => {
        if (color.startsWith('#')) {
            const rgb = hexToRgb(color);
            if (rgb) {
                setResolvedPrimaryColor(rgb);
            }
        } else {
            setResolvedPrimaryColor(color);
        }
    };

    return (
        <ThemeContext.Provider
            value={{
                theme: { mode, primaryColor },
                setThemeMode,
                setPrimaryColor,
                isSaving,
                saveError: appearanceSaveError,
            }}
        >
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
}
